import {
  MIN_PASSWORD_LENGTH,
  ROLE_LABELS,
  createUserSchema,
  resetPasswordSchema,
  type Role,
  type UserDto,
} from '@pietrochip/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as api from '../api/endpoints';
import { ApiError, describeError } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useToasts } from '../components/Toasts';
import { Field, Modal, Notice } from '../components/ui';
import { date } from '../lib/format';

/**
 * Gestione degli account, solo per gli amministratori.
 *
 * Gli utenti non si eliminano, si disattivano: un account cancellato
 * porterebbe via con sé la tracciabilità delle operazioni che ha fatto, e il
 * registro delle restituzioni perderebbe i nomi. La disattivazione ottiene la
 * stessa cosa — la persona non entra più — senza rompere lo storico.
 */
export function UsersPage() {
  const { user: currentUser } = useAuth();
  const { push } = useToasts();

  const [users, setUsers] = useState<UserDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState<UserDto | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { users: list } = await api.listUsers();
      setUsers(list);
      setError(null);
    } catch (cause) {
      setError(describeError(cause));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const sorted = useMemo(() => {
    if (users === null) return null;
    return [...users].sort((a, b) => {
      if (a.active !== b.active) return a.active ? -1 : 1;
      if (a.role !== b.role) return a.role === 'admin' ? -1 : 1;
      return a.name.localeCompare(b.name, 'it');
    });
  }, [users]);

  async function patch(target: UserDto, changes: { role?: Role; active?: boolean }) {
    setBusyId(target.id);
    try {
      const { user } = await api.updateUser(target.id, changes);
      setUsers((current) => current?.map((entry) => (entry.id === user.id ? user : entry)) ?? current);
      push({ tone: 'neutral', message: `${user.name} aggiornato.` });
    } catch (cause) {
      // Il caso interessante è l'ultimo amministratore: il server lo rifiuta
      // con un messaggio già pronto, e va mostrato così com'è.
      push({ tone: cause instanceof ApiError ? 'missing' : 'danger', message: describeError(cause) });
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-head__title">Utenti</h1>
          <p className="page-head__meta">
            {sorted === null
              ? 'Caricamento…'
              : `${sorted.filter((entry) => entry.active).length} attivi su ${sorted.length}`}
          </p>
        </div>
        <div className="page-head__actions">
          <button type="button" className="btn btn--primary" onClick={() => setCreating(true)}>
            Nuovo utente
          </button>
        </div>
      </div>

      {error && <Notice tone="danger">{error}</Notice>}

      {sorted && (
        <div className="card">
          {/*
            Sei colonne non stanno su un telefono: la tabella scorre dentro la
            card invece di allargare la pagina.
          */}
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Email</th>
                  <th>Ruolo</th>
                  <th>Stato</th>
                  <th>Creato il</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {sorted.map((entry) => {
                  const isSelf = entry.id === currentUser?.id;
                  const busy = busyId === entry.id;

                  return (
                    <tr key={entry.id}>
                      <td>
                        <strong>{entry.name}</strong>
                        {isSelf && <span className="faint"> · sei tu</span>}
                      </td>
                      <td className="mono" style={{ fontSize: '0.875rem' }}>
                        {entry.email}
                      </td>
                      <td>{ROLE_LABELS[entry.role]}</td>
                      <td>
                        <span
                          className={`state ${entry.active ? 'state--returned' : 'state--missing'}`}
                        >
                          <span className="state__mark" aria-hidden="true" />
                          {entry.active ? 'Attivo' : 'Disattivato'}
                        </span>
                      </td>
                      <td className="faint">{date(entry.createdAt)}</td>
                      <td>
                        <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                          <button
                            type="button"
                            className="btn btn--sm btn--quiet"
                            onClick={() => setResetting(entry)}
                          >
                            Password
                          </button>

                          <button
                            type="button"
                            className="btn btn--sm btn--quiet"
                            disabled={busy}
                            onClick={() =>
                              void patch(entry, {
                                role: entry.role === 'admin' ? 'operator' : 'admin',
                              })
                            }
                          >
                            {entry.role === 'admin' ? 'Rendi operatore' : 'Rendi amministratore'}
                          </button>

                          <button
                            type="button"
                            className={`btn btn--sm ${entry.active ? '' : 'btn--primary'}`}
                            disabled={busy}
                            onClick={() => void patch(entry, { active: !entry.active })}
                          >
                            {entry.active ? 'Disattiva' : 'Riattiva'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {creating && (
        <CreateUserModal
          onClose={() => setCreating(false)}
          onCreated={(created) => {
            setUsers((current) => (current ? [...current, created] : [created]));
            setCreating(false);
            push({ tone: 'neutral', message: `Account creato per ${created.name}.` });
          }}
        />
      )}

      {resetting && (
        <ResetPasswordModal
          user={resetting}
          onClose={() => setResetting(null)}
          onDone={() => {
            setResetting(null);
            push({
              tone: 'neutral',
              message: `${resetting.name} dovrà accedere con la nuova password.`,
            });
          }}
        />
      )}
    </>
  );
}

function CreateUserModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (user: UserDto) => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role>('operator');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit() {
    const parsed = createUserSchema.safeParse({ name, email, password, role });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Controlla i dati inseriti.');
      return;
    }

    setBusy(true);
    setError(null);

    try {
      const { user } = await api.createUser(parsed.data);
      onCreated(user);
    } catch (cause) {
      setError(describeError(cause));
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Nuovo utente"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Annulla
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={busy}
            onClick={() => void onSubmit()}
          >
            {busy ? 'Creazione…' : 'Crea account'}
          </button>
        </>
      }
    >
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          void onSubmit();
        }}
      >
        <Field label="Nome">
          {({ id, ...aria }) => (
            <input
              {...aria}
              id={id}
              className="input"
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={120}
              autoComplete="off"
              required
            />
          )}
        </Field>

        <Field label="Email">
          {({ id, ...aria }) => (
            <input
              {...aria}
              id={id}
              className="input"
              type="email"
              inputMode="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="off"
              autoCapitalize="none"
              required
            />
          )}
        </Field>

        <Field
          label="Password iniziale"
          hint={`Almeno ${MIN_PASSWORD_LENGTH} caratteri. Comunicala a voce: non viene inviata nessuna email.`}
        >
          {({ id, ...aria }) => (
            <input
              {...aria}
              id={id}
              className="input"
              type="text"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="new-password"
              autoCapitalize="none"
              spellCheck={false}
              minLength={MIN_PASSWORD_LENGTH}
              required
            />
          )}
        </Field>

        <Field label="Ruolo" hint="Un amministratore gestisce utenti ed eventi; un operatore lavora sui banchi.">
          {({ id, ...aria }) => (
            <select
              {...aria}
              id={id}
              className="select"
              value={role}
              onChange={(event) => setRole(event.target.value as Role)}
            >
              <option value="operator">{ROLE_LABELS.operator}</option>
              <option value="admin">{ROLE_LABELS.admin}</option>
            </select>
          )}
        </Field>

        {error && <Notice tone="danger">{error}</Notice>}
      </form>
    </Modal>
  );
}

function ResetPasswordModal({
  user,
  onClose,
  onDone,
}: {
  user: UserDto;
  onClose: () => void;
  onDone: () => void;
}) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit() {
    const parsed = resetPasswordSchema.safeParse({ password });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Controlla la password.');
      return;
    }

    setBusy(true);
    setError(null);

    try {
      await api.resetUserPassword(user.id, parsed.data);
      onDone();
    } catch (cause) {
      setError(describeError(cause));
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`Password di ${user.name}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Annulla
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={busy}
            onClick={() => void onSubmit()}
          >
            {busy ? 'Salvataggio…' : 'Imposta la password'}
          </button>
        </>
      }
    >
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          void onSubmit();
        }}
      >
        <Notice>
          Impostando una nuova password, le sessioni aperte di {user.name} vengono chiuse: dovrà
          accedere di nuovo anche dal tablet del banco.
        </Notice>

        <Field label="Nuova password" hint={`Almeno ${MIN_PASSWORD_LENGTH} caratteri.`}>
          {({ id, ...aria }) => (
            <input
              {...aria}
              id={id}
              className="input"
              type="text"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="new-password"
              autoCapitalize="none"
              spellCheck={false}
              minLength={MIN_PASSWORD_LENGTH}
              required
            />
          )}
        </Field>

        {error && <Notice tone="danger">{error}</Notice>}
      </form>
    </Modal>
  );
}
